import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { License } from '@portal-core/licenses/models/license.model';
import { LicensesService } from '@portal-core/licenses/services/licenses.service';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { LoadingState } from '@portal-core/util/loading-state';
import { Observable, Subscription, catchError, map, of } from 'rxjs';

interface LicenseOpenAIForm {
  key: FormControl<string>,
  version: FormControl<string>
}

interface ModelOption {
  text: string,
  value: string
}

@Component({
  selector: 'mc-license-open-ai-form',
  templateUrl: './license-open-ai-form.component.html',
  styleUrls: ['./license-open-ai-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
@AutoUnsubscribe()
export class LicenseOpenAIFormComponent implements OnInit {

  @Input() license: License;
  @Output() cancel = new EventEmitter<void>();

  hide: boolean = true;
  loadingState: LoadingState<string> = new LoadingState<string>();

  AIForm: FormGroup<LicenseOpenAIForm>;

  models: ModelOption[] = [
    { text: 'GPT-5', value: 'gpt-5' },
    { text: 'GPT-5 mini', value: 'gpt-5-mini' },
    { text: 'GPT-4o', value: 'gpt-4o' },
    { text: 'GPT-4', value: 'gpt-4' },
    { text: 'GPT-3.5', value: 'gpt-3.5-turbo' },
  ];

  versionSubscription: Subscription;
  settingsSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private licensesService: LicensesService,
    private errorService: ErrorService
  ) { }

  ngOnInit() {
    this.buildForm();
    this.versionSubscription = this.AIForm.controls.version.valueChanges.subscribe(() => this.AIForm.controls.key.updateValueAndValidity());

    this.loadingState.update(true);

    // The key is not included on the license model; fetch it from the ServerManagement-gated endpoint to pre-populate the form.
    this.settingsSubscription = this.licensesService.getOpenAISettings$(this.license.Id).subscribe(settings => {
      this.AIForm.patchValue({
        key: settings.ApiKey ?? '',
        version: settings.ApiModel ? settings.ApiModel : 'gpt-4o'
      });
      this.loadingState.update(false);
    }, error => {
      this.loadingState.update(false, 'There was a problem loading the OpenAI settings.', this.errorService.getErrorMessages(error));
    });
  }

  submitFn = (): Observable<MadCloudResult<any>> => {
    return this.licensesService.changeOpenAISettings$(this.license.Id, this.AIForm.value.key, this.AIForm.value.version);
  }

  private buildForm() {
    this.AIForm = this.formBuilder.group<LicenseOpenAIForm>({
      key: new FormControl('', { asyncValidators: [this.createValidateOpenAI()] }),
      version: new FormControl('gpt-4o')
    });
  }

  private createValidateOpenAI(): AsyncValidatorFn {
    return (control: FormControl): Observable<ValidationErrors> => {
      const [apiKey, apiModel] = [
        control?.value,
        this.AIForm?.controls?.version?.value
      ];
      if (apiKey && apiModel) {
        return this.licensesService.verifyOpenAIKey$(apiKey, apiModel).pipe(
          map(() => null),
          catchError(() => {
            return of({ apiKey: true });
          })
        );
      } else {
        return of(null);
      }
    };
  }
}
